// Thin, defensive wrapper around the CrazyGames SDK (v3). EVERY function is a no-op
// unless the SPA is running inside the CrazyGames portal (see portal.ts), so this file
// has zero effect on itch.io or direct logins — the SDK script is never even loaded.
//
// The SDK powers CrazyGames' ads (interstitial "midgame" + optional rewarded) and
// their session analytics via lifecycle events (loadingStop / gameplayStart / stop /
// happytime). All calls are wrapped in try/catch so an SDK/API mismatch degrades to
// "no ads" rather than breaking the game. If CrazyGames changes their v3 API, only
// this file needs adjusting.
import { useSyncExternalStore } from 'react';
import { isCrazyGames } from './portal';
import { authApi } from './api';

const SDK_SRC = 'https://sdk.crazygames.com/crazygames-sdk-v3.js';
// A midgame (interstitial) ad won't be shown if ANY ad — interstitial OR rewarded —
// was shown within this window, so we don't nag the player.
const AD_COOLDOWN_MS = 5 * 60 * 1000;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnySdk = any;

let sdk: AnySdk = null;
let ready = false;
let initPromise: Promise<void> | null = null;
let lastAdAt = 0; // last time any ad (midgame/rewarded) was shown or a rewarded ad was chosen

// Ads master switch, driven by the server config (CrazyGames:AdsEnabled, default false).
// When false NO ad ever runs — even on CrazyGames — so the game can ship ad-free and
// have ads flipped on later purely via config. Reactive so ad buttons show/hide once
// the config resolves.
let adsEnabled = false;
const adsListeners = new Set<() => void>();
function setAdsEnabled(v: boolean) {
  if (adsEnabled === v) return;
  adsEnabled = v;
  adsListeners.forEach(l => l());
}
export function areAdsEnabled(): boolean { return adsEnabled; }

// React hook: true only when running on CrazyGames AND ads are enabled by config.
// Use it to gate the rewarded-ad buttons.
export function useAdsEnabled(): boolean {
  return useSyncExternalStore(
    (cb) => { adsListeners.add(cb); return () => adsListeners.delete(cb); },
    () => isCrazyGames && adsEnabled,
    () => false,
  );
}

function safe(fn: () => void) { try { fn(); } catch { /* SDK best-effort */ } }

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) { resolve(); return; }
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('sdk load failed'));
    document.head.appendChild(s);
  });
}

// Load + initialise the SDK. Memoised so it runs once; awaiting it always resolves
// after init has settled (needed by the CrazyGames login bootstrap). No-op off CrazyGames.
export function initCrazyGames(): Promise<void> {
  if (!isCrazyGames) return Promise.resolve();
  if (!initPromise) initPromise = doInit();
  return initPromise;
}

async function doInit(): Promise<void> {
  // Resolve the ads master switch before anything can request an ad. Analytics /
  // lifecycle events below run regardless; only ads are gated on this flag.
  try {
    const res = await authApi.getConfig();
    setAdsEnabled(!!(res.data as { adsEnabled?: boolean }).adsEnabled);
  } catch {
    setAdsEnabled(false); // no config → no ads
  }
  try {
    await loadScript(SDK_SRC);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    sdk = (window as any).CrazyGames?.SDK ?? null;
    if (!sdk) return;
    await sdk.init?.();
    ready = true;
    // The SPA has finished loading; begin an active-gameplay window.
    safe(() => sdk.game?.loadingStop?.());
    safe(() => sdk.game?.gameplayStart?.());
  } catch {
    // SDK unavailable — the game runs fine, just without ads/analytics.
  }
}

// ── User module (CrazyGames login) — all no-ops / null off CrazyGames or if the SDK
//    is unavailable, so callers degrade to guest play. ─────────────────────────────
export interface CrazyUser { username: string; profilePictureUrl?: string }

export async function isUserAccountAvailable(): Promise<boolean> {
  if (!ready || !sdk?.user?.isUserAccountAvailable) return false;
  try { return await sdk.user.isUserAccountAvailable(); } catch { return false; }
}

export async function getCrazyUser(): Promise<CrazyUser | null> {
  if (!ready || !sdk?.user?.getUser) return null;
  try { return (await sdk.user.getUser()) ?? null; } catch { return null; }
}

export async function getCrazyUserToken(): Promise<string | null> {
  if (!ready || !sdk?.user?.getUserToken) return null;
  try { return (await sdk.user.getUserToken()) ?? null; } catch { return null; }
}

// Opens the CrazyGames login/register popup. Resolves true only if the user completed
// it (a token is then available); false if cancelled/unavailable.
export async function showCrazyAuthPrompt(): Promise<boolean> {
  if (!ready || !sdk?.user?.showAuthPrompt) return false;
  try { await sdk.user.showAuthPrompt(); return true; } catch { return false; }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function addCrazyAuthListener(cb: (user: any) => void) {
  if (ready && sdk?.user?.addAuthListener) safe(() => sdk.user.addAuthListener(cb));
}

export function gameplayStart() { if (ready) safe(() => sdk.game?.gameplayStart?.()); }
export function gameplayStop()  { if (ready) safe(() => sdk.game?.gameplayStop?.()); }
// Signal a natural, positive break point (CrazyGames may show an ad here).
export function happytime()     { if (ready) safe(() => sdk.game?.happytime?.()); }

// Request an interstitial ad at a natural break, rate-limited so it never nags:
// skipped if any ad was shown in the last AD_COOLDOWN_MS. Pauses/resumes gameplay.
export async function requestMidgameAd(): Promise<void> {
  if (!adsEnabled) return;
  if (!ready || !sdk?.ad?.requestAd) return;
  if (Date.now() - lastAdAt < AD_COOLDOWN_MS) return;
  lastAdAt = Date.now();
  try {
    safe(() => sdk.game?.gameplayStop?.());
    await new Promise<void>((resolve) => {
      let settled = false;
      const done = () => { if (!settled) { settled = true; resolve(); } };
      sdk.ad.requestAd('midgame', { adStarted: () => {}, adFinished: done, adError: done });
      setTimeout(done, 30_000); // safety net so we never hang the gameplay window
    });
  } catch {
    /* ignore */
  } finally {
    safe(() => sdk.game?.gameplayStart?.());
  }
}

// Show a REWARDED ad (player opted in). Resolves true only if the ad was fully
// watched (→ grant the bonus); false if unavailable/failed/closed early (→ base
// reward). Choosing a rewarded ad resets the interstitial cooldown regardless of the
// outcome, so a following normal completion won't immediately trigger a midgame ad.
export async function requestRewardedAd(): Promise<boolean> {
  if (!adsEnabled) return false;
  if (!ready || !sdk?.ad?.requestAd) return false;
  lastAdAt = Date.now();
  try {
    safe(() => sdk.game?.gameplayStop?.());
    return await new Promise<boolean>((resolve) => {
      let settled = false;
      const finish = (ok: boolean) => { if (!settled) { settled = true; resolve(ok); } };
      sdk.ad.requestAd('rewarded', {
        adStarted: () => {},
        adFinished: () => finish(true),
        adError: () => finish(false),
      });
      setTimeout(() => finish(false), 60_000); // safety net
    });
  } catch {
    return false;
  } finally {
    safe(() => sdk.game?.gameplayStart?.());
  }
}
